import { WORKBENCH_CONTENT_SCHEMA_INVALID } from "./contentSchemas";

const STABLE_CODE = /^[A-Za-z][A-Za-z0-9_.-]{0,159}$/u;

export interface WorkbenchContentQueryFailure {
  code: string;
  httpStatus: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asStableCode(value: string) {
  const code = value.trim();
  return STABLE_CODE.test(code) ? code : null;
}

function readHttpStatus(error: unknown) {
  if (!isRecord(error)) return null;
  if (typeof error.status === "number" && Number.isInteger(error.status)) {
    return error.status;
  }
  if (
    typeof error.originalStatus === "number" &&
    Number.isInteger(error.originalStatus)
  ) {
    return error.originalStatus;
  }
  return null;
}

function readApiErrorCode(error: unknown) {
  if (!isRecord(error)) return null;
  const data = error.data;
  if (typeof data === "string") {
    const fromData = asStableCode(data);
    if (fromData) return fromData;
  }
  if (isRecord(data)) {
    if (typeof data.error === "string") {
      const fromError = asStableCode(data.error);
      if (fromError) return fromError;
    }
    if (isRecord(data.error) && typeof data.error.code === "string") {
      const fromNested = asStableCode(data.error.code);
      if (fromNested) return fromNested;
    }
  }
  if (typeof error.message === "string") {
    const fromMessage = asStableCode(error.message);
    if (fromMessage) return fromMessage;
  }
  if (typeof error.error === "string") {
    return asStableCode(error.error);
  }
  return null;
}

function fallbackCode(error: unknown, httpStatus: number | null) {
  if (!isRecord(error)) {
    return httpStatus ? `http_${httpStatus}` : "workbench_content_unavailable";
  }
  if (error.status === "FETCH_ERROR") return "workbench_content_network_error";
  if (error.status === "TIMEOUT_ERROR") return "workbench_content_timeout";
  if (error.status === "CUSTOM_ERROR") return WORKBENCH_CONTENT_SCHEMA_INVALID;
  if (error.status === "PARSING_ERROR") {
    return httpStatus ? `http_${httpStatus}` : "workbench_content_response_invalid";
  }
  return httpStatus ? `http_${httpStatus}` : "workbench_content_unavailable";
}

export function readWorkbenchContentQueryFailure(
  error: unknown,
  options: { skipped: boolean },
): WorkbenchContentQueryFailure | null {
  if (options.skipped || error == null) return null;
  const httpStatus = readHttpStatus(error);
  return {
    code: readApiErrorCode(error) ?? fallbackCode(error, httpStatus),
    httpStatus,
  };
}
